import { useMemo } from 'react';
import { Avatar, Space, Typography } from 'antd';

const { Text } = Typography;

interface UseUserOptionRenderOptions {
  allowedUserTypes?: string[];
  showUserType?: boolean;
  size?: 'small' | 'default' | 'large';
}

export const useUserOptionRender = (options: UseUserOptionRenderOptions = {}) => {
  const { 
    allowedUserTypes = [], 
    showUserType = true, 
    size = 'small' 
  } = options;

  return useMemo(
    () => (user: any) => {
      // Filter users by allowed types if specified
      if (allowedUserTypes.length > 0) {
        const userType = (user.type || user.role)?.toLowerCase();
        if (!allowedUserTypes.map(type => type.toLowerCase()).includes(userType)) {
          return null; // Don't render this option
        }
      }

      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const initials = fullName
        .split(' ')
        .map((name: string) => name.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);

      // Get user type with pastel color mapping
      const getUserTypeConfig = (type: string) => {
        const typeConfigs: {
          [key: string]: { color: string; bgColor: string; label: string };
        } = {
          admin: { color: '#722ed1', bgColor: '#f9f0ff', label: 'Admin' },
          doctor: { color: '#1890ff', bgColor: '#e6f7ff', label: 'Doctor' },
          master_admin: {
            color: '#52c41a',
            bgColor: '#f6ffed',
            label: 'Master Admin',
          },
          therapist: {
            color: '#fa8c16',
            bgColor: '#fff7e6',
            label: 'Therapist',
          },
          counsellor: {
            color: '#eb2f96',
            bgColor: '#fff0f6',
            label: 'Counsellor',
          },
          receptionist: {
            color: '#13c2c2',
            bgColor: '#e6fffb',
            label: 'Receptionist',
          },
          marketing: {
            color: '#fa541c',
            bgColor: '#fff2e8',
            label: 'Marketing',
          },
          default: { color: '#8c8c8c', bgColor: '#f5f5f5', label: 'User' },
        };

        return typeConfigs[type?.toLowerCase()] || typeConfigs.default;
      };

      const typeConfig = getUserTypeConfig(user.type || user.role);
      const avatarSize = size === 'small' ? 'small' : size === 'large' ? 'large' : 'default';

      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            gap: 8,
          }}
        >
          <Space>
            <Avatar size={avatarSize} style={{ backgroundColor: '#1890ff' }}>
              {user.avatar ? (
                <img src={user.avatar} alt={fullName} />
              ) : (
                <span style={{ fontSize: size === 'small' ? '12px' : '14px' }}>
                  {initials}
                </span>
              )}
            </Avatar>
            <Text style={{ fontSize: size === 'small' ? '14px' : '16px' }}>
              {fullName || 'Unknown User'}
            </Text>
          </Space>
          {showUserType && (
            <div
              style={{
                backgroundColor: typeConfig.bgColor,
                color: typeConfig.color,
                border: `1px solid ${typeConfig.color}20`,
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: '11px',
                fontWeight: 500,
                lineHeight: '1.2',
                whiteSpace: 'nowrap',
              }}
            >
              {typeConfig.label}
            </div>
          )}
        </div>
      );
    },
    [allowedUserTypes, showUserType, size]
  );
};